import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import {
  useEnvironment,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import { FormViewerContext, FormViewerHandle } from '@wix/form-viewer/widget';
import {
  FormValues,
  type FormActions,
  type FormConfig,
  type FormResults,
} from '@wix/form-viewer';
import { Text } from '@wix/wix-ui-tpa';
import { st, classes } from './Widget.st.css';
import { WaiverForms, WaiverFormStatus } from '../../../types/form-types';
import WaiverForm from './WaiverForm';
import { ViewMode } from '../controller';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';
import { useWaiverSignWidgetBI, BI_ACTION, BI_VALUE } from '../../../bi';

export type ControllerProps = {
  status: WaiverFormStatus;
  formId?: string;
  waiverForms?: {
    initialValues: Record<string, any>;
    overrides: Record<string, any>;
    fieldProperties: Record<string, any>;
  };
  formErrors?: { messageKey: string; field: string }[];
  onSubmitSuccess?: (submission: FormSubmission) => void;
  onLightboxClose?: () => void;
  viewMode?: ViewMode;
} & WidgetProps & {
    wfActions: FormActions;
    wfConfig: FormConfig;
    wfResults: FormResults;
  };

const Widget: FC<WidgetProps<ControllerProps>> = ({
  status,
  formId,
  waiverForms,
  formErrors,
  onSubmitSuccess,
  onLightboxClose,
  viewMode,
  ...widgetProps
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { WaiverFormActionsBI } = useWaiverSignWidgetBI();

  const [waiverFormValues, setWaiverFormValues] = useState<FormValues>(
    waiverForms?.initialValues || {},
  );

  const waiverFormRef = useRef<FormViewerHandle>(null);
  const hasInitializedRef = useRef(false);
  const hasReportedLoadRef = useRef(false);

  useEffect(() => {
    if (
      status === WaiverFormStatus.IDLE &&
      formId &&
      waiverForms &&
      !hasReportedLoadRef.current
    ) {
      hasReportedLoadRef.current = true;
      WaiverFormActionsBI({
        action: BI_ACTION.LOAD,
        value: BI_VALUE.EMPTY,
      });
    }
  }, [status, formId, waiverForms, WaiverFormActionsBI]);

  useEffect(() => {
    if (waiverForms?.initialValues && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setWaiverFormValues(waiverForms.initialValues);
    }
  }, [waiverForms?.initialValues]);

  const handleWaiverFormChange = useCallback((values: FormValues) => {
    setWaiverFormValues(values);
  }, []);

  const isOverlay = viewMode === ViewMode.OVERLAY;
  const isFormReady = formId && waiverForms;
  const showForm =
    status === WaiverFormStatus.IDLE || status === WaiverFormStatus.VALIDATING;

  return (
    <div
      data-hook="WaiverForm-wrapper"
      className={st(classes.root, { isOverlay, isMobile })}
    >
      <div className={classes.formContainer}>
        {status === WaiverFormStatus.INITIALIZING && (
          <div className={classes.loadingContainer}>
            <Text className={classes.loadingText}>
              {t('waivers-sign-form.loading')}
            </Text>
          </div>
        )}

        {status === WaiverFormStatus.ERROR && (
          <div className={classes.errorContainer}>
            <Text className={classes.errorText}>
              {formErrors && formErrors.length > 0
                ? formErrors.map((error, index) => (
                    <Text key={index} tagName="div">
                      {t(error.messageKey)}
                    </Text>
                  ))
                : t('waivers-sign-form.error-loading')}
            </Text>
          </div>
        )}

        {showForm && isFormReady && (
          <FormViewerContext
            host={widgetProps.host}
            wfActions={widgetProps.wfActions}
            wfConfig={widgetProps.wfConfig}
            wfResults={widgetProps.wfResults}
          >
            <WaiverForm
              formId={formId}
              waiverFormRef={waiverFormRef}
              waiverForms={waiverForms as WaiverForms}
              formValues={waiverFormValues}
              handleFormChange={handleWaiverFormChange}
              onSubmitSuccess={onSubmitSuccess}
              onLightboxClose={onLightboxClose}
              viewMode={viewMode}
            />
          </FormViewerContext>
        )}
      </div>
    </div>
  );
};

export default Widget;
